<template>
    <vx-modal v-model:visible="visible" size="sm" padding="standard">
        <template v-slot:header>Cancel run</template>
        <template v-slot:subheader>Run #{{ props.run.id }} · {{ props.run.team_name }}</template>

        <div class="flex flex-col gap-2 text-sm text-slate-800">
            <strong>Are you sure you want to cancel this run?</strong>
            <p class="m-0" data-test="cancel-explanation">{{ explanation }}</p>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="visible = false"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Go back
                </vx-button>
                <vx-button
                    @click="confirm()"
                    data-test="confirm-cancel"
                    :loading="submitting"
                    type="button"
                    class="grow"
                    size="lg"
                    color="danger"
                >
                    Cancel run
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { VxButton, VxModal } from '@voxie/frontend-components';
import { isAxiosError } from 'axios';
import { computed, ref } from 'vue';
import { isCancellableStatus, type RndCancellableStatus } from '~/components/kiosk/kiosk-reassigned-numbers/rndLabels';
import { useToasted } from '~/composables/useToasted';
import { adminRndClient } from '~/services';
import type { RndRun } from '~/services/adminRndClient.types';
import { HTTP_CONFLICT } from '~/support/HttpStatuses';
import { catchRequestError } from '~/utils/request';

const props = defineProps<{ run: RndRun }>();
const emit = defineEmits<{ cancelled: [run: RndRun]; conflict: [] }>();
const visible = defineModel<boolean>('visible', { default: false });

const toasted = useToasted();

const explanations: Record<RndCancellableStatus, string> = {
    applying:
        'It stops once the resets under way finish. Contacts already reset stay reset, and the rest are left alone. It ends as Partially applied, cancelled by you, and Check remaining contacts can apply the remaining resets.',
    awaiting_approval:
        'Nobody is reset. It ends as Stopped, cancelled by you, and Check remaining contacts can follow it up and apply the resets this run decided. To end it for good instead, deny its resets.',
    querying:
        'It stops once the requests already sent to RND return, and those stay billed. Nobody is reset, not even the contacts it has already found to be reassigned. It ends as Stopped, cancelled by you, and Check remaining contacts can follow it up: that run applies the resets this one decided and queries the contacts it did not reach.',
    queued: 'Its contacts are still loading and nothing has been queried. It ends as Cancelled and can never be followed up.',
};

const submitting = ref(false);

const explanation = computed(() => (isCancellableStatus(props.run.status) ? explanations[props.run.status] : ''));

const confirm = async () => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;

    try {
        const { data } = await adminRndClient.cancelRun(props.run.id);

        emit('cancelled', data);
        visible.value = false;
    } catch (error) {
        toasted.error(catchRequestError(error), { sticky: true });

        if (isAxiosError(error) && HTTP_CONFLICT === error.response?.status) {
            emit('conflict');
            visible.value = false;
        }
    } finally {
        submitting.value = false;
    }
};
</script>
